import { ok } from '@atcute/client';

import { useQuery } from '@tanstack/react-query';

import { getClients, useSession } from '#/state/session';

import { STALE } from '..';

const RQKEY_ROOT = 'convo-unread-counts';
export const RQKEY = (includeGroupChats: boolean) => [RQKEY_ROOT, includeGroupChats] as const;
export const RQKEY_PARTIAL = [RQKEY_ROOT] as const;

// the server sentinel-caps the badge counts: unreadAcceptedConvos and
// unreadRequestConvos max out at 100 (meaning "more than 99"). at the cap the
// value is no longer an exact count, so consumers must not treat it as one —
// both the optimistic decrement and the badge display ceiling key off these.
export const UNREAD_ACCEPTED_CAP = 100;
export const UNREAD_REQUEST_CAP = 100;

export function useUnreadCountsQuery() {
	const { hasSession } = useSession();
	const { chat } = getClients();
	// no age assurance in this fork, so group chats are always counted
	const includeGroupChats = true;

	return useQuery({
		queryKey: RQKEY(includeGroupChats),
		enabled: hasSession,
		staleTime: STALE.SECONDS.FIFTEEN,
		queryFn: async ({ signal }) => {
			if (!chat) {
				throw new Error('Not signed in');
			}
			const data = await ok(
				chat.get('chat.bsky.convo.getUnreadCounts', {
					signal,
					params: { includeGroupChats },
				}),
			);
			return data;
		},
	});
}

/** @returns the message badge state. */
export function useUnreadMessageCount(): {
	count: number;
	numUnread?: string;
	hasNew: boolean;
} {
	const { data } = useUnreadCountsQuery();
	const accepted = data?.unreadAcceptedConvos ?? 0;
	const request = data?.unreadRequestConvos ?? 0;

	if (accepted > 0) {
		return {
			count: accepted,
			// the API cap represents overflow.
			numUnread:
				accepted >= UNREAD_ACCEPTED_CAP
					? String(UNREAD_ACCEPTED_CAP - 1) + '+'
					: String(Math.min(accepted, UNREAD_ACCEPTED_CAP - 1)),
			hasNew: false,
		};
	} else if (request > 0) {
		return {
			count: 1,
			numUnread: undefined,
			hasNew: true,
		};
	} else {
		return {
			count: 0,
			numUnread: undefined,
			hasNew: false,
		};
	}
}
